import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { ShieldCheck, AlertTriangle, Download, Plus, Search, FileText, X, MapPin, Syringe, Scissors, CheckCircle2 } from "lucide-react";

const STATUS_OPTIONS = [
  { v: "in_care", l: "In Care", c: "bg-sun text-ink" },
  { v: "released", l: "Released", c: "bg-brand-green text-white" },
  { v: "adopted", l: "Adopted", c: "bg-brand text-white" },
  { v: "deceased", l: "Deceased", c: "bg-ink text-white" },
  { v: "transferred", l: "Transferred", c: "bg-brand-blue text-white" },
];

export default function NgoRegister() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [query, setQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    const q = new URLSearchParams();
    if (filterStatus) q.set("status", filterStatus);
    if (query) q.set("search", query);
    axios.get(`${API}/ngo/register-entries?${q}`).then(r => setItems(r.data.items)).catch(() => setItems([]));
    axios.get(`${API}/ngo/register/compliance-summary`).then(r => setSummary(r.data)).catch(() => setSummary(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus, query]);

  const downloadCsv = async () => {
    const r = await axios.get(`${API}/ngo/register/export.csv`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "petbond_animal_register.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    const r = await axios.get(`${API}/ngo/register/export.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "petbond_animal_register.pdf"; a.click(); URL.revokeObjectURL(url);
  };

  if (user?.role !== "ngo") return (
    <div className="bg-surface border border-line rounded-3xl p-8 text-center">
      <h2 className="font-heading text-2xl font-extrabold">NGO account required</h2>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="ngo-register-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold inline-flex items-center gap-1.5"><ShieldCheck className="w-3 h-3"/> ABC Rules 2023 · Court-Mandated</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Animal Register</h1>
          <p className="text-ink-soft text-sm max-w-xl">Inspection-ready statutory log of every animal under your custody. Auto-syncs sterilization + vaccination records from PetBond Vets.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={downloadPdf} data-testid="export-pdf-btn" className="bg-brand text-white rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5">
            <Download className="w-4 h-4"/> Export PDF
          </button>
          <button onClick={downloadCsv} data-testid="export-csv-btn" className="bg-ink text-white rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5">
            <Download className="w-4 h-4"/> CSV
          </button>
          <button onClick={() => setCreating(true)} data-testid="new-entry-btn" className="bg-brand text-white rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4"/> New entry
          </button>
        </div>
      </header>

      {summary && (
        <section className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="register-summary">
          <Kpi label="Animals" value={summary.totals.animals} accent="bg-brand text-white"/>
          <Kpi label="Sterilized (ABC)" value={summary.totals.sterilized} accent="bg-brand-blue text-white" icon={Scissors}/>
          <Kpi label="Anti-Rabies" value={summary.totals.anti_rabies} accent="bg-brand-green text-white" icon={Syringe}/>
          <Kpi label="Released" value={summary.totals.released} accent="bg-leaf text-ink"/>
          <Kpi label="Court-ready" value={`${summary.totals.court_ready}/${summary.totals.animals || 0}`} accent={summary.totals.court_ready === summary.totals.animals && summary.totals.animals > 0 ? "bg-brand-green text-white" : "bg-sun text-ink"} icon={ShieldCheck}/>
        </section>
      )}

      {summary && summary.avg_compliance_pct < 100 && summary.totals.animals > 0 && (
        <div className="bg-brand/10 border border-brand/30 rounded-2xl p-4 flex items-start gap-3" data-testid="compliance-banner">
          <AlertTriangle className="w-5 h-5 text-brand mt-0.5"/>
          <div className="flex-1">
            <div className="font-bold">Average compliance: {summary.avg_compliance_pct}%</div>
            <div className="text-xs text-ink-soft mt-0.5">Entries with missing required fields are flagged below. Complete them before next inspection.</div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[220px] bg-surface border border-line rounded-full px-4 py-2 flex items-center gap-2">
          <Search className="w-4 h-4 text-ink-soft"/>
          <input data-testid="register-search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search tag #, address, microchip…" className="flex-1 outline-none bg-transparent font-bold text-sm"/>
        </div>
        {["", ...STATUS_OPTIONS.map(o => o.v)].map(s => (
          <button key={s || "all"} data-testid={`reg-filter-${s || "all"}`} onClick={() => setFilterStatus(s)} className={`px-3 py-1.5 rounded-full text-xs font-bold border ${filterStatus === s ? "bg-ink text-white border-ink" : "bg-surface border-line text-ink-soft"}`}>
            {s ? STATUS_OPTIONS.find(o => o.v === s).l : "All"}
          </button>
        ))}
      </div>

      <div className="bg-surface border border-line rounded-3xl overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-surface-muted text-left">
            <tr>
              <th className="p-3">Tag #</th>
              <th className="p-3">Species · Sex</th>
              <th className="p-3">Capture</th>
              <th className="p-3">ABC · ARV</th>
              <th className="p-3">Status</th>
              <th className="p-3">Compliance</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(e => (
              <tr key={e.register_entry_id} data-testid={`reg-row-${e.register_entry_id}`} className="border-t border-line hover:bg-surface-muted/40">
                <td className="p-3 font-extrabold">{e.tag_number}</td>
                <td className="p-3"><div>{e.species}</div><div className="text-xs text-ink-soft">{e.sex || "—"} · {e.age_estimate || "—"}</div></td>
                <td className="p-3"><div className="text-xs">{e.capture_date}</div><div className="text-xs text-ink-soft inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{(e.capture_address || "").slice(0, 24)}…</div></td>
                <td className="p-3">
                  <div className="flex items-center gap-1 text-xs"><Scissors className="w-3 h-3"/> <Badge state={e.sterilization_status}/></div>
                  <div className="flex items-center gap-1 text-xs mt-1"><Syringe className="w-3 h-3"/> <Badge state={e.arv_status}/></div>
                </td>
                <td className="p-3">
                  <span className={`${STATUS_OPTIONS.find(o => o.v === e.current_status)?.c || "bg-surface-muted"} rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider`}>{STATUS_OPTIONS.find(o => o.v === e.current_status)?.l || e.current_status}</span>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 rounded-full bg-surface-muted overflow-hidden"><div className={`h-full ${e.compliance.court_ready ? "bg-brand-green" : "bg-brand"}`} style={{width: `${e.compliance.score_pct}%`}}/></div>
                    <span className="text-xs font-bold">{e.compliance.score_pct}%</span>
                    {e.compliance.court_ready && <CheckCircle2 className="w-4 h-4 text-brand-green"/>}
                  </div>
                </td>
                <td className="p-3 text-right">
                  <button data-testid={`reg-edit-${e.register_entry_id}`} onClick={() => setEditing(e)} className="text-brand font-bold text-xs">Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && (
          <div className="p-10 text-center text-ink-soft">
            <FileText className="w-8 h-8 mx-auto opacity-50 mb-2"/>
            No entries yet. Click "New entry" to log your first animal.
          </div>
        )}
      </div>

      {creating && <EntryModal onClose={() => { setCreating(false); load(); }}/>}
      {editing && <EntryModal entry={editing} onClose={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function Kpi({ label, value, accent, icon: Icon }) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-3">
      {Icon && <div className={`w-7 h-7 rounded-lg grid place-items-center ${accent}`}><Icon className="w-3.5 h-3.5"/></div>}
      <div className="font-heading text-2xl font-extrabold mt-2">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wider text-ink-soft font-bold">{label}</div>
    </div>
  );
}

function Badge({ state }) {
  const map = { done: "bg-brand-green text-white", pending: "bg-sun text-ink", exempt: "bg-surface-muted text-ink-soft", not_applicable: "bg-surface-muted text-ink-soft" };
  return <span className={`${map[state] || "bg-surface-muted"} rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider`}>{state}</span>;
}

function EntryModal({ entry, onClose }) {
  const isEdit = !!entry;
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [form, setForm] = useState(entry || {
    tag_number: "", species: "dog", sex: "male", age_estimate: "", colour_markings: "",
    capture_date: new Date().toISOString().slice(0, 10), capture_address: "", capture_lat: null, capture_lng: null,
    captured_by: "", complaint_reference: "",
    sterilization_status: "pending", sterilization_date: "", sterilization_vet_name: "", surgical_notes: "",
    arv_status: "pending", arv_date: "", arv_batch_number: "", arv_manufacturer: "", arv_vet_name: "",
    release_date: "", release_address: "", release_lat: null, release_lng: null,
    microchip_id: "", notch_ear: "", current_status: "in_care", notes: "", intake_photo: null,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const cleaned = { ...form };
    ["capture_lat", "capture_lng", "release_lat", "release_lng"].forEach(k => { if (cleaned[k] === "" || cleaned[k] === null || isNaN(Number(cleaned[k]))) delete cleaned[k]; else cleaned[k] = Number(cleaned[k]); });
    if (isEdit) {
      await axios.patch(`${API}/ngo/register-entries/${entry.register_entry_id}`, cleaned);
    } else {
      await axios.post(`${API}/ngo/register-entries`, cleaned);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-2xl border border-line shadow-soft max-h-[90vh] overflow-y-auto" data-testid="register-entry-form">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading text-xl font-extrabold">{isEdit ? `Edit ${form.tag_number}` : "Register a new animal"}</h3>
            {isEdit && form.compliance && (
              <div className="text-xs mt-1">Compliance: <span className="font-bold">{form.compliance.score_pct}%</span> · Missing: {form.compliance.missing.join(", ") || "✓ Complete"}</div>
            )}
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5"/></button>
        </div>

        <section>
          <h4 className="text-xs uppercase tracking-wider font-bold text-brand">Identity</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
            <Field label="Tag # *"><input required data-testid="reg-tag" value={form.tag_number} onChange={e => set("tag_number", e.target.value)} className="inp"/></Field>
            <Field label="Species">
              <select value={form.species} onChange={e => set("species", e.target.value)} className="inp">
                <option value="dog">Dog</option><option value="cat">Cat</option><option value="other">Other</option>
              </select>
            </Field>
            <Field label="Sex">
              <select value={form.sex || ""} onChange={e => set("sex", e.target.value)} className="inp">
                <option value="">—</option><option value="male">Male</option><option value="female">Female</option><option value="unknown">Unknown</option>
              </select>
            </Field>
            <Field label="Age estimate"><input value={form.age_estimate || ""} onChange={e => set("age_estimate", e.target.value)} className="inp"/></Field>
            <Field label="Colour / markings"><input value={form.colour_markings || ""} onChange={e => set("colour_markings", e.target.value)} className="inp"/></Field>
            <Field label="Current status">
              <select value={form.current_status} onChange={e => set("current_status", e.target.value)} className="inp">
                {STATUS_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </Field>
          </div>
        </section>

        <section className="mt-4">
          <h4 className="text-xs uppercase tracking-wider font-bold text-brand">Capture record *</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
            <Field label="Capture date *"><input required type="date" value={form.capture_date} onChange={e => set("capture_date", e.target.value)} className="inp"/></Field>
            <Field label="Address *" wide><input required value={form.capture_address} onChange={e => set("capture_address", e.target.value)} className="inp"/></Field>
          </div>
        </section>

        <section className="mt-4">
          <h4 className="text-xs uppercase tracking-wider font-bold text-brand">Sterilization (ABC) *</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
            <Field label="Status">
              <select data-testid="reg-ster-status" value={form.sterilization_status} onChange={e => set("sterilization_status", e.target.value)} className="inp">
                <option value="pending">Pending</option><option value="done">Done</option><option value="not_applicable">Not Applicable</option><option value="exempt">Exempt</option>
              </select>
            </Field>
            <Field label="Date"><input type="date" value={form.sterilization_date || ""} onChange={e => set("sterilization_date", e.target.value)} className="inp"/></Field>
            <Field label="Vet name"><input value={form.sterilization_vet_name || ""} onChange={e => set("sterilization_vet_name", e.target.value)} className="inp"/></Field>
          </div>
        </section>

        <section className="mt-4">
          <h4 className="text-xs uppercase tracking-wider font-bold text-brand">Anti-Rabies Vaccination *</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
            <Field label="Status">
              <select data-testid="reg-arv-status" value={form.arv_status} onChange={e => set("arv_status", e.target.value)} className="inp">
                <option value="pending">Pending</option><option value="done">Done</option><option value="exempt">Exempt</option>
              </select>
            </Field>
            <Field label="Date"><input type="date" value={form.arv_date || ""} onChange={e => set("arv_date", e.target.value)} className="inp"/></Field>
            <Field label="Batch #"><input value={form.arv_batch_number || ""} onChange={e => set("arv_batch_number", e.target.value)} className="inp"/></Field>
          </div>
        </section>

        <button type="button" onClick={() => setAdvancedOpen(!advancedOpen)} className="mt-4 text-sm font-bold text-brand" data-testid="reg-toggle-advanced">
          {advancedOpen ? "▼" : "▶"} Advanced fields (GPS, release, microchip, notes)
        </button>

        {advancedOpen && (
          <section className="mt-3 space-y-4">
            <div>
              <h4 className="text-xs uppercase tracking-wider font-bold text-ink-soft">GPS coordinates</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                <Field label="Capture lat"><input type="number" step="0.0001" value={form.capture_lat ?? ""} onChange={e => set("capture_lat", e.target.value)} className="inp"/></Field>
                <Field label="Capture lng"><input type="number" step="0.0001" value={form.capture_lng ?? ""} onChange={e => set("capture_lng", e.target.value)} className="inp"/></Field>
                <Field label="Release lat"><input type="number" step="0.0001" value={form.release_lat ?? ""} onChange={e => set("release_lat", e.target.value)} className="inp"/></Field>
                <Field label="Release lng"><input type="number" step="0.0001" value={form.release_lng ?? ""} onChange={e => set("release_lng", e.target.value)} className="inp"/></Field>
              </div>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-wider font-bold text-ink-soft">Release & identifiers</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                <Field label="Release date"><input type="date" value={form.release_date || ""} onChange={e => set("release_date", e.target.value)} className="inp"/></Field>
                <Field label="Release address" wide><input value={form.release_address || ""} onChange={e => set("release_address", e.target.value)} className="inp"/></Field>
                <Field label="Microchip ID"><input value={form.microchip_id || ""} onChange={e => set("microchip_id", e.target.value)} className="inp"/></Field>
                <Field label="Ear notch">
                  <select value={form.notch_ear || ""} onChange={e => set("notch_ear", e.target.value)} className="inp">
                    <option value="">—</option><option value="left">Left</option><option value="right">Right</option><option value="both">Both</option><option value="none">None</option>
                  </select>
                </Field>
                <Field label="Complaint ref"><input value={form.complaint_reference || ""} onChange={e => set("complaint_reference", e.target.value)} className="inp"/></Field>
                <Field label="Captured by"><input value={form.captured_by || ""} onChange={e => set("captured_by", e.target.value)} className="inp"/></Field>
              </div>
            </div>
            <Field label="Surgical / vet notes"><textarea rows={2} value={form.surgical_notes || ""} onChange={e => set("surgical_notes", e.target.value)} className="inp"/></Field>
            <Field label="Other notes"><textarea rows={2} value={form.notes || ""} onChange={e => set("notes", e.target.value)} className="inp"/></Field>
          </section>
        )}

        <button data-testid="reg-submit" className="mt-5 w-full bg-brand text-white rounded-full py-3 font-bold">{isEdit ? "Save changes" : "Add to register"}</button>
        <style>{`.inp{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.5rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.inp:focus{border-color:#FF6B35;}`}</style>
      </form>
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <label className={`block ${wide ? "md:col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider font-bold text-ink-soft">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
